import {
  ShoppingCart,
  Package,
  Tag,
  Store,
  WifiOff,
  Users,
  BarChart3,
  Layers,
  Martini,
  Repeat,
  Undo2,
  Handshake,
} from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import FeaturesHeroV2 from '@/components/marketing/features/v2/FeaturesHeroV2';
import FeatureAnchorStripV2 from '@/components/marketing/features/v2/FeatureAnchorStripV2';
import FeatureModuleSection from '@/components/marketing/features/v2/FeatureModuleSection';
import ComparePromoBandV2 from '@/components/marketing/features/v2/ComparePromoBandV2';
import BarTeaserDemo from '@/components/marketing/bar/v2/BarTeaserDemo';
import { FinalCtaSection } from '@/components/marketing/v2/FinalCtaSection';
import {
  SalesDemo,
  StockDemo,
  CatalogDemo,
  MultiShopDemo,
  OfflineDemo,
  TeamDemo,
  ReportsDemo,
  PlatformsDemo,
} from '@/components/marketing/features/v2/FeatureDemos';

const MODULES: { name: string; description: string }[] = [
  { name: 'Ventes', description: 'Encaissement rapide, scan code-barres, paiement mixte Mobile Money + espèces, impression thermique.' },
  { name: 'Stock', description: 'Inventaire temps réel, seuils d’alerte, mouvements tracés (entrées, sorties, transferts, pertes).' },
  { name: 'Catalogue', description: 'Catégories, variantes, SKU, gestion des prix et génération de codes-barres.' },
  { name: 'Multi-boutique', description: 'Plusieurs points de vente, un seul tableau de bord, transferts de stock et droits par rôle.' },
  { name: 'Hors ligne', description: 'La caisse continue de vendre sans Internet ; synchronisation automatique au retour du réseau.' },
  { name: 'Équipe', description: 'Connexion par code PIN, droits par utilisateur, journal d’audit et commissions.' },
  { name: 'Rapports', description: 'Ventes, marges, rotation du stock, classement produits, export PDF/Excel, rapport Z.' },
  { name: 'Multi-plateforme', description: 'Desktop (Windows/macOS/Linux), Android et Web responsive, synchronisés.' },
  { name: 'Velko Bar', description: 'Contrôle du coût réel des boissons : recettes décimales, inventaire à l’aveugle, variance.' },
  { name: 'Troc & reprise', description: 'Accepte un article en paiement : reprises multiples, appoint et rendu monnaie automatiques, restock immédiat.' },
  { name: 'Retours & remboursements', description: 'Retours, échanges et remboursements (espèces, Mobile Money, avoir) avec validation gérant et anti-sur-remboursement.' },
  { name: 'Partenaires', description: 'Dépôt-vente dans les deux sens : stock confié suivi, quota par partenaire, facture avec QR code et garantie produit.' },
];

const FEATURES_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Fonctionnalités de Velko POS',
  itemListElement: MODULES.map((m, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: m.name,
    description: m.description,
  })),
};

function MiniDemoCard({
  tag,
  rows,
  footerLabel,
  footerValue,
}: {
  tag: string;
  rows: ReadonlyArray<{ label: string; value: string; accent?: boolean }>;
  footerLabel: string;
  footerValue: string;
}) {
  return (
    <div className="w-full max-w-sm border-[3px] border-charcoal-800 bg-charcoal-800 p-6 text-white shadow-neobrutal-lime-10">
      <div className="border-b border-charcoal-700 pb-3 font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-200">
        {tag}
      </div>
      <ul className="mt-4 space-y-2.5">
        {rows.map((row) => (
          <li
            key={row.label}
            className="flex items-center justify-between gap-3 border-[1.5px] border-charcoal-700 px-3 py-2.5 font-marketing-body text-[13.5px]"
          >
            <span className="text-charcoal-100">{row.label}</span>
            <span
              className={
                row.accent
                  ? 'font-marketing-mono text-[12px] font-semibold text-terracotta-400'
                  : 'font-marketing-mono text-[12px] font-semibold text-lime-400'
              }
            >
              {row.value}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-center justify-between border-2 border-lime-500 bg-charcoal-900 px-3 py-2.5 font-marketing-mono text-[11px] uppercase tracking-[0.06em]">
        <span className="text-charcoal-200">{footerLabel}</span>
        <span className="font-semibold text-lime-400">{footerValue}</span>
      </div>
    </div>
  );
}

const TrocDemo = () => (
  <MiniDemoCard
    tag="Vente · avec reprise"
    rows={[
      { label: 'Smartphone neuf', value: '120 000 F' },
      { label: 'Reprise · ancien', value: '−45 000 F', accent: true },
    ]}
    footerLabel="Reste à payer"
    footerValue="75 000 F"
  />
);

const ReturnsDemo = () => (
  <MiniDemoCard
    tag="Retour · vente #1042"
    rows={[
      { label: 'Chemise (taille M)', value: '12 000 F' },
      { label: 'Remboursé · avoir', value: '12 000 F' },
    ]}
    footerLabel="Au-delà du seuil"
    footerValue="Validation gérant"
  />
);

const PartnersDemo = () => (
  <MiniDemoCard
    tag="Consignation · Aïcha"
    rows={[
      { label: 'Confiés', value: '×20' },
      { label: 'En dépôt', value: '×8' },
      { label: 'Solde dû', value: '96 000 F' },
    ]}
    footerLabel="Facture"
    footerValue="QR code"
  />
);

export default function Features() {
  return (
    <>
      <SEOHead
        title="Fonctionnalités — tous les modules POS inclus | Velko"
        description="Caisse, stock, catalogue, multi-boutique, mobile money, équipe, rapports, multi-plateforme, Velko Bar, troc & reprise, retours & remboursements, partenaires. Tous les modules Velko sont inclus, dès Starter."
        path="/fonctionnalites"
        ogImage="/brand/og/og-features.png"
        jsonLd={FEATURES_JSON_LD}
        breadcrumbs={[
          { name: 'Accueil', path: '/' },
          { name: 'Fonctionnalités', path: '/fonctionnalites' },
        ]}
      />

      <FeaturesHeroV2 />
      <FeatureAnchorStripV2 />

      <FeatureModuleSection
        id="f1"
        num="01 / Ventes"
        eyebrow="Caisse enregistreuse"
        sectionLabel="Au quotidien"
        title={
          <>
            Encaisse en <span className="underline-lime">3 secondes</span>.
          </>
        }
        description="Du scan à l'imprimé, rien ne t'arrête. Orange Money, MTN, Wave, espèces, mixte : tout rentre dans le même ticket."
        bullets={[
          'Scan code-barres + saisie rapide avec raccourcis clavier',
          'Paiement mixte Mobile Money + cash sur un même ticket',
          'Impression thermique 58/80 mm et ticket WhatsApp',
          'Remises, avoirs et tickets en attente',
        ]}
        demo={<SalesDemo />}
        Icon={ShoppingCart}
        tone="paper"
      />

      <FeatureModuleSection
        id="f2"
        num="02 / Stock"
        eyebrow="Inventaire temps réel"
        sectionLabel="Toujours à jour"
        title={
          <>
            Tu sais <span className="underline-lime">toujours</span> ce qu'il te reste.
          </>
        }
        description="Les niveaux se mettent à jour à chaque vente, chaque réception. Tu reçois l'alerte avant que ça casse, pas après."
        bullets={[
          "Seuils d'alerte par produit et par boutique",
          'Mouvements tracés : entrées, sorties, transferts, pertes',
          'Inventaires rapides avec scanner',
        ]}
        demo={<StockDemo />}
        Icon={Package}
        reversed
        tone="white"
      />

      <FeatureModuleSection
        id="f3"
        num="03 / Catalogue"
        eyebrow="Fiches produits"
        sectionLabel="Structure claire"
        title={
          <>
            Ton catalogue <span className="underline-lime">propre</span>, sans Excel.
          </>
        }
        description="Catégories, photos, variantes (taille, couleur, parfum), codes-barres imprimables. Prix en F CFA, TVA en option."
        bullets={[
          'Import CSV pour migrer tes produits existants',
          "Variantes illimitées et gestion des prix d'achat",
          'Génération de codes-barres et étiquettes prix',
        ]}
        demo={<CatalogDemo />}
        Icon={Tag}
        tone="paper"
      />

      <FeatureModuleSection
        id="f4"
        num="04 / Multi-boutique"
        eyebrow="Réseau commercial"
        sectionLabel="Un seul tableau"
        title={
          <>
            Plusieurs boutiques. <span className="underline-lime">Un seul tableau de bord.</span>
          </>
        }
        description="Suis tes points de vente, compare les performances, transfère le stock entre magasins. Chaque équipe ne voit que sa boutique."
        bullets={[
          'Consolidation automatique CA + marge par boutique',
          'Transferts de stock tracés boutique à boutique',
          "Droits d'accès par rôle et par boutique",
        ]}
        demo={<MultiShopDemo />}
        Icon={Store}
        reversed
        tone="white"
      />

      <FeatureModuleSection
        id="f5"
        num="05 / Offline"
        eyebrow="Tolérance réseau"
        sectionLabel="Ça marche toujours"
        title={
          <>
            Coupure réseau ? <span className="underline-lime">Tu continues à vendre.</span>
          </>
        }
        description="La caisse fonctionne à 100% hors ligne. Les ventes partent en file d'attente chiffrée, et se synchronisent dès que le réseau revient."
        bullets={[
          'Base de données locale chiffrée SQLCipher',
          'Résolution automatique des conflits à la synchro',
          'Badge visible sur chaque écran : Online / Offline / Sync',
        ]}
        demo={<OfflineDemo />}
        Icon={WifiOff}
        tone="paper"
      />

      <FeatureModuleSection
        id="f6"
        num="06 / Équipe"
        eyebrow="Gestion des employés"
        sectionLabel="Sécurité terrain"
        title={
          <>
            Chacun ses droits. <span className="underline-lime">Chaque vente tracée.</span>
          </>
        }
        description="Caissier, responsable, gérant : chaque rôle voit exactement ce qu'il doit voir. Connexion par code PIN à 4 chiffres — pas de mot de passe à retenir."
        bullets={[
          "Connexion PIN rapide sur l'écran tactile",
          "Journal d'audit : qui a vendu, remisé, annulé",
          'Objectifs individuels et commissions',
        ]}
        demo={<TeamDemo />}
        Icon={Users}
        reversed
        tone="white"
      />

      <FeatureModuleSection
        id="f7"
        num="07 / Rapports"
        eyebrow="Tableau de bord"
        sectionLabel="Au-delà des chiffres"
        title={
          <>
            Pas juste des chiffres. <span className="underline-lime">Des décisions.</span>
          </>
        }
        description="CA, marge brute, top produits, rotation stock, ventes par employé, heures creuses : les bons indicateurs, pas 42 graphiques inutiles."
        bullets={[
          'Export PDF et Excel en un clic',
          'Rapport Z de caisse automatique à la fermeture',
          'Comparaison J-1, S-1, M-1 · vue saisonnière',
        ]}
        demo={<ReportsDemo />}
        Icon={BarChart3}
        tone="paper"
      />

      <FeatureModuleSection
        id="f8"
        num="08 / Multi-plateforme"
        eyebrow="Web · Desktop · Mobile"
        sectionLabel="Partout où tu vas"
        title={
          <>
            Desktop au comptoir. <span className="underline-lime">Mobile sur le terrain.</span>
          </>
        }
        description="Même compte, même stock, mêmes clients. Ouvre la caisse au PC, prends la commande sur le téléphone, gère depuis ton navigateur à la maison."
        bullets={[
          'App desktop Tauri · Windows, macOS, Linux',
          'App mobile Flutter · Android (iOS bientôt)',
          'Tableau de bord Web responsive pour le gérant',
        ]}
        demo={<PlatformsDemo />}
        Icon={Layers}
        reversed
        tone="white"
      />

      <FeatureModuleSection
        id="f9"
        num="09 / Velko Bar"
        eyebrow="Module premium · contrôle des coûts"
        sectionLabel="Pour les bars & restaurants"
        title={
          <>
            Le coût réel de <span className="underline-lime">chaque verre.</span>
          </>
        }
        description="Pour les bars, lounges et restaurants : recettes décimales, consommation déduite à chaque vente (même hors ligne), inventaire à l'aveugle et écart chiffré en F CFA. Façon WISK, intégré à ta caisse."
        bullets={[
          'Pour cost % et marge réelle par cocktail',
          "Détection du surdosage, du coulage et du vol",
          'Dashboards séparés pour le gérant et les investisseurs',
        ]}
        demo={<BarTeaserDemo />}
        Icon={Martini}
        tone="paper"
        cta={{ label: 'Découvrir Velko Bar', to: '/velko-bar' }}
      />

      <FeatureModuleSection
        id="f10"
        num="10 / Troc & reprise"
        eyebrow="Article en paiement"
        sectionLabel="Conclure plus de ventes"
        title={
          <>
            Accepte la reprise. <span className="underline-lime">Calcule rien.</span>
          </>
        }
        description="Le client paie une partie avec un ancien article ? Velko prend la reprise, calcule l'appoint dans les deux sens (rendu monnaie inclus) et remet l'occasion en stock — même hors ligne."
        bullets={[
          'Reprises multiples sur une même vente',
          'Appoint et rendu monnaie automatiques (cash_out)',
          "Restock immédiat de l'article repris, tout tracé",
        ]}
        demo={<TrocDemo />}
        Icon={Repeat}
        reversed
        tone="white"
        cta={{ label: 'Découvrir le troc & la reprise', to: '/troc' }}
      />

      <FeatureModuleSection
        id="f11"
        num="11 / Retours & remboursements"
        eyebrow="Retour, échange, avoir"
        sectionLabel="Une caisse toujours juste"
        title={
          <>
            Des retours <span className="underline-lime">sous contrôle.</span>
          </>
        }
        description="Retour total ou partiel, échange avec différentiel, remboursement en espèces, Mobile Money ou avoir client. Avec validation du gérant au-delà d'un seuil et protection anti-sur-remboursement."
        bullets={[
          'Remboursement espèces · Mobile Money · avoir client',
          "Seuil d'approbation gérant + annulation 24 h",
          'Restock maîtrisé et statut « remboursé » automatique',
        ]}
        demo={<ReturnsDemo />}
        Icon={Undo2}
        tone="paper"
        cta={{ label: 'Découvrir la gestion des retours', to: '/gestion-retours' }}
      />

      <FeatureModuleSection
        id="f12"
        num="12 / Partenaires"
        eyebrow="Dépôt-vente & consignation"
        sectionLabel="Étendre la force de vente"
        title={
          <>
            Le dépôt-vente, <span className="underline-lime">enfin traçable.</span>
          </>
        }
        description="Confie des produits à des partenaires ou source en consignation chez un grossiste. Velko suit le stock confié, les soldes dus, applique un quota et génère une facture avec QR code consultable partout."
        bullets={[
          'Consignation dans les deux sens (donner / sourcer)',
          'Quota par partenaire + suivi des soldes dus',
          'Facture avec QR code et garantie produit optionnelle',
        ]}
        demo={<PartnersDemo />}
        Icon={Handshake}
        reversed
        tone="white"
        cta={{ label: 'Découvrir les partenaires', to: '/partenaires' }}
      />

      <ComparePromoBandV2 />

      <FinalCtaSection
        eyebrow="Démarrage immédiat"
        title="Ta caisse."
        highlight="Prête dans 3 minutes."
        subtitle="Sans carte bancaire. Sans engagement. Tu testes tous les modules pendant 14 jours."
        primaryLabel="Commencer gratuit"
        primaryTo="/register"
        secondaryLabel="Parler à un humain"
        secondaryTo="/contact"
        guarantees={['14 jours d’essai', 'Sans CB', 'Support FR', 'Sans engagement']}
      />
    </>
  );
}
